/* eslint-disable max-classes-per-file */
import moment from "moment";
import { Period } from "~app/common/models/period";
import { ObjectUtils } from "~app/common/utils/object-utils";
import { Virkeskostnad } from "./virkeskostnader/virkeskostnader-models";

export interface VKLeveransvirkessplanDTO {
    kontraktId: number;
    kontraktGuid?: string;
    kontraktAffarsform: string;
    kontraktGiltighetFrom?: string;
    kontraktGiltighetTom?: string;
    anbudGiltighetFrom?: string;
    anbudGiltighetTom?: string;
    atgarderForeslagetProduktionsdatum: VKAtgardProduktionsdatumDTO[];
    leveransplanHandelssortiment: VKLeveransplanHandelssortimentDTO[];
}

export interface VKLeveransplanHandelssortimentDTO {
    virkeskostnadId: number;
    virkeskostnadGUID: string;
    handelssortimentId?: number;
    handelssortimentNamn: string;
    handelssortimentGruppId?: number;
    handelssortimentGruppNamn: string;
    handelssortimentGruppSekvensnummer?: number;
    utbyteInlastKvantitet: number;
    leveransplan: VKLeveransplanDTO[];
}

export interface VKLeveransplanDTO {
    id: number;
    virkeskostnadId: number;
    virkeskostnadGUID: string;
    kvantitet: number;
    period: string;
}

export interface VKAtgardProduktionsdatumDTO {
    atgardId: number;
    guid: string;
    periodFrom: string;
    periodTom: string;
}

export class VirkeskostnadDataRow {
    id: number;
    guid: string;
    handelssortimentNamn: string;
    handelssortimentsgruppNamn: string;
    mattslag: string;
    summaTotalt: number;
    summaForkalkyl: number;
    leveransplan: Leveransplan[];
}
export class Leveransvirkessplan {
    kontraktId: number;
    kontraktGuid?: string;
    kontraktAffarsform: string;
    kontraktGiltighetFrom?: Date;
    kontraktGiltighetTom?: Date;
    anbudGiltighetFrom?: Date;
    anbudGiltighetTom?: Date;
    atgarderForeslagetProduktionsdatum: AtgardProduktionsdatum[];
    leveransplanHandelssortiment: LeveransplanHandelssortiment[];

    constructor(p?: VKLeveransvirkessplanDTO) {
        this.kontraktId = p?.kontraktId;
        this.kontraktGuid = p?.kontraktGuid;
        this.kontraktAffarsform = p?.kontraktAffarsform ?? "";
        this.kontraktGiltighetFrom = moment(p?.kontraktGiltighetFrom).toDate();
        this.kontraktGiltighetTom = moment(p?.kontraktGiltighetTom).toDate();
        this.anbudGiltighetFrom = moment(p?.anbudGiltighetFrom).toDate();
        this.anbudGiltighetTom = moment(p?.anbudGiltighetTom).toDate();
        this.atgarderForeslagetProduktionsdatum = p?.atgarderForeslagetProduktionsdatum.map(x => new AtgardProduktionsdatum(x));
        this.leveransplanHandelssortiment = p?.leveransplanHandelssortiment?.map(x => new LeveransplanHandelssortiment(x));
    }

    findOrCreateLeveransplaner(virkeskostnad: Virkeskostnad): Leveransplan[] {
        const hsleveransplan = this.leveransplanHandelssortiment?.find(x =>
            ObjectUtils.isNullOrUndefined(virkeskostnad.handelssortimentId)
                ? x.handelssortimentGruppId === virkeskostnad.handelssortimentsgruppId
                : x.handelssortimentId === virkeskostnad.handelssortimentId);

        if (ObjectUtils.hasValue(hsleveransplan)) {
            return hsleveransplan.leveransplan;
        }

        const leveransplanHandelssortiment = new LeveransplanHandelssortiment();
        leveransplanHandelssortiment.virkeskostnadId = virkeskostnad.id;
        leveransplanHandelssortiment.virkeskostnadGUID = virkeskostnad.guid;
        leveransplanHandelssortiment.handelssortimentId = virkeskostnad.handelssortimentId;
        leveransplanHandelssortiment.handelssortimentNamn = virkeskostnad.handelssortimentNamn;
        leveransplanHandelssortiment.handelssortimentGruppId = virkeskostnad.handelssortimentsgruppId;
        leveransplanHandelssortiment.handelssortimentGruppNamn = virkeskostnad.handelssortimentsgruppNamn;
        leveransplanHandelssortiment.handelssortimentGruppSekvensnummer = virkeskostnad.handelssortimentsgruppSekvensnummer;
        leveransplanHandelssortiment.utbyteInlastKvantitet = virkeskostnad.utbyteInlastKvantitet;
        leveransplanHandelssortiment.leveransplan = [];
        this.leveransplanHandelssortiment.push(leveransplanHandelssortiment);

        return leveransplanHandelssortiment.leveransplan;
    }

    isPeriodValid(): boolean {
        const fromDate = moment(this.kontraktGiltighetFrom).isValid() || moment(this.kontraktGiltighetTom).isValid()
            ? this.kontraktGiltighetFrom : this.anbudGiltighetFrom;
        const tomDate = moment(this.kontraktGiltighetFrom).isValid() || moment(this.kontraktGiltighetTom).isValid()
            ? this.kontraktGiltighetTom
            : this.anbudGiltighetTom;

        return fromDate &&
            tomDate &&
            moment(fromDate)
                .isBefore(tomDate);
    }

    getAnbudKontraktPeriod(): Period {
        const fromDate = moment(this.kontraktGiltighetFrom).isValid() || moment(this.kontraktGiltighetTom).isValid()
            ? this.kontraktGiltighetFrom : this.anbudGiltighetFrom;
        const tomDate = moment(this.kontraktGiltighetFrom).isValid() || moment(this.kontraktGiltighetTom).isValid()
            ? this.kontraktGiltighetTom
            : this.anbudGiltighetTom;

        const period = new Period();
        period.periodFrom = moment(fromDate).toDate();
        period.periodTom = moment(tomDate).toDate();

        return period;
    }

    canFordelaLeveransplan(): boolean {
        const invalidAtgarder = this.atgarderForeslagetProduktionsdatum?.filter(p => !p?.periodFrom || !p?.periodTom);
        // eslint-disable-next-line sonarjs/prefer-single-boolean-return
        if (invalidAtgarder?.length > 0 || ObjectUtils.isNullOrUndefined(this.atgarderForeslagetProduktionsdatum) || this.atgarderForeslagetProduktionsdatum.length === 0) {
            return false;
        }
        return true;
    }
}

export class AtgardProduktionsdatum extends Period {
    atgardId: number;
    guid: string;

    constructor(p?: VKAtgardProduktionsdatumDTO) {
        super();
        if (ObjectUtils.hasValue(p)) {
            this.periodFrom = moment(p.periodFrom).toDate();
            this.periodTom = moment(p.periodTom).toDate();
            this.atgardId = p?.atgardId;
            this.guid = p?.guid;
        }
    }
}
export class LeveransplanHandelssortiment {
    virkeskostnadId: number;
    virkeskostnadGUID: string;
    handelssortimentId?: number;
    handelssortimentNamn: string;
    handelssortimentGruppId?: number;
    handelssortimentGruppNamn: string;
    handelssortimentGruppSekvensnummer?: number;
    utbyteInlastKvantitet: number;
    leveransplan: Leveransplan[];

    constructor(p?: VKLeveransplanHandelssortimentDTO) {
        this.virkeskostnadId = p?.virkeskostnadId;
        this.virkeskostnadGUID = p?.virkeskostnadGUID;
        this.handelssortimentId = p?.handelssortimentId;
        this.handelssortimentNamn = p?.handelssortimentNamn;
        this.handelssortimentGruppId = p?.handelssortimentGruppId;
        this.handelssortimentGruppNamn = p?.handelssortimentGruppNamn;
        this.handelssortimentGruppSekvensnummer = p?.handelssortimentGruppSekvensnummer;
        this.utbyteInlastKvantitet = p?.utbyteInlastKvantitet;
        this.leveransplan = p?.leveransplan.map(lp => new Leveransplan(lp));
    }
}

export class Leveransplan {
    id: number;
    virkeskostnadId: number;
    virkeskostnadGUID: string;
    kvantitet: number;
    period: Date;

    constructor(p?: VKLeveransplanDTO) {
        this.id = p?.id;
        this.virkeskostnadId = p?.virkeskostnadId;
        this.virkeskostnadGUID = p?.virkeskostnadGUID;
        this.kvantitet = p.kvantitet;
        this.period = moment(p?.period).toDate();
    }
}

export interface IFordelaLeveransplanQuery {
    atgarder: IFordelaLeveransplanAtgard[];
}

export interface IFordelaLeveransplanAtgard {
    produktionsdatumFrom: string;
    produktionsdatumTom: string;
    volymer: IFordelaLeveransplanVolym[];
}

export interface IFordelaLeveransplanVolym {
    handelssortimentsgruppId: number;
    handelssortimentId: number;
    kvantitet: number;
}

export interface IFordelaLeveransplanResponse {
    leveransplanHandelssortiment: ILeveransvolymHandelssortiment[];
}

export interface ILeveransvolymHandelssortiment {
    handelssortimentId: number;
    handelssortimentsgruppId: number;
    volymer: ILeveransvolym[];
}

export interface ILeveransvolym {
    period: string;
    kvantitet: number;
}
